/**
 * Workflow API Service
 * Handles state.json, plan.json, and workflow-related API calls
 */

import {
  StateJson,
  PlanJson,
  ResourceIndexEntry,
  SlideProgress,
} from '@/types/workflow';
import api from './api';

/**
 * Get state.json for a conversation
 */
export async function getState(conversationId: string): Promise<StateJson | null> {
  try {
    const response = await api.get(`/project/${conversationId}/state`);
    return response.data.state || null;
  } catch (error: any) {
    if (error.response?.status === 404) return null;
    console.error('Error fetching state.json:', error);
    return null;
  }
}

/**
 * Check if plan.json exists by checking state.json progress.planCreated flag
 */
export async function checkPlanExists(conversationId: string): Promise<boolean> {
  try {
    const state = await getState(conversationId);
    return state?.progress?.planCreated === true;
  } catch (error) {
    console.error('Error checking if plan exists:', error);
    return false;
  }
}

/**
 * Get planning phase status from state.json
 * Used as secondary check for workflow state recovery
 */
export async function getPlanningStatus(conversationId: string): Promise<{
  planCreated: boolean;
  planningComplete: boolean;
}> {
  try {
    const state = await getState(conversationId);
    return {
      planCreated: state?.progress?.planCreated ?? false,
      planningComplete: state?.progress?.planningPhaseComplete ?? false,
    };
  } catch (error) {
    console.error('Error getting planning status:', error);
    return {
      planCreated: false,
      planningComplete: false,
    };
  }
}

/**
 * Update state.json for a conversation
 */
export async function updateState(
  conversationId: string,
  updates: Partial<StateJson>
): Promise<boolean> {
  try {
    await api.put(`/conversations/${conversationId}/state`, updates);
    return true;
  } catch (error) {
    console.error('Error updating state.json:', error);
    return false;
  }
}

/**
 * Get plan.json for a conversation
 */
export async function getPlan(conversationId: string): Promise<PlanJson | null> {
  try {
    const response = await api.get(`/conversations/${conversationId}/plan`);
    return response.data;
  } catch (error: any) {
    if (error.response?.status === 404) return null;
    console.error('Error fetching plan.json:', error);
    return null;
  }
}

/**
 * Update plan.json for a conversation
 */
export async function updatePlan(
  conversationId: string,
  plan: PlanJson
): Promise<boolean> {
  try {
    await api.put(`/conversations/${conversationId}/plan`, plan);
    return true;
  } catch (error) {
    console.error('Error updating plan.json:', error);
    return false;
  }
}

/**
 * Get resource-index.json for a conversation
 */
export async function getResourceIndex(
  conversationId: string
): Promise<ResourceIndexEntry[] | null> {
  try {
    const response = await api.get(`/conversations/${conversationId}/resources/index`);
    return response.data.resources || [];
  } catch (error: any) {
    if (error.response?.status === 404) return null;
    console.error('Error fetching resource-index.json:', error);
    return null;
  }
}

/**
 * Start resource analysis
 */
export async function startResourceAnalysis(conversationId: string): Promise<boolean> {
  try {
    await api.post(`/conversations/${conversationId}/workflow/analyze-resources`);
    return true;
  } catch (error) {
    console.error('Error starting resource analysis:', error);
    return false;
  }
}

/**
 * Generate plan.json
 */
export async function generatePlan(
  conversationId: string,
  instructions?: string
): Promise<PlanJson | null> {
  try {
    const response = await api.post(
      `/conversations/${conversationId}/workflow/generate-plan`,
      { instructions }
    );
    return response.data;
  } catch (error) {
    console.error('Error generating plan:', error);
    return null;
  }
}

/**
 * Generate a single slide
 */
export async function generateSlide(
  conversationId: string,
  slideId: string
): Promise<SlideProgress | null> {
  try {
    const response = await api.post(
      `/conversations/${conversationId}/workflow/generate-slide/${slideId}`
    );
    return response.data;
  } catch (error) {
    console.error(`Error generating slide ${slideId}:`, error);
    return null;
  }
}

/**
 * Generate all slides
 */
export async function generateAllSlides(conversationId: string, regenerate = false): Promise<{
  success: boolean;
  jobsCreated: number;
  jobs: Array<{ slideId: string; jobId: string | null; status: string }>;
} | null> {
  try {
    const response = await api.post(`/conversations/${conversationId}/slides/generate-all`, {
      regenerate,
    });
    return response.data;
  } catch (error) {
    console.error('Error generating all slides:', error);
    return null;
  }
}

/**
 * Get slides with their current status
 */
export async function getSlides(conversationId: string): Promise<SlideProgress[] | null> {
  try {
    const response = await api.get(`/conversations/${conversationId}/slides`);
    return response.data.slides || [];
  } catch (error: any) {
    if (error.response?.status === 404) return null;
    console.error('Error fetching slides:', error);
    return null;
  }
}

/**
 * Merge state.json and plan.json to create unified slide list
 */
export async function getMergedSlideData(
  conversationId: string
): Promise<SlideProgress[] | null> {
  try {
    console.log('🔄 [WorkflowAPI] Merging slide data for:', conversationId);
    const [state, plan] = await Promise.all([
      getState(conversationId),
      getPlan(conversationId),
    ]);

    console.log('📝 [WorkflowAPI] Data fetched:', {
      hasState: !!state,
      hasPlan: !!plan,
      stateSlides: state?.slides?.length || 0,
      planSlides: plan?.slides?.length || 0
    });

    if (!plan) {
      console.warn('⚠️ [WorkflowAPI] No plan.json found');
      return null;
    }

    // Merge plan data with state data
    const slides: SlideProgress[] = plan.slides.map((planSlide, index) => {
      const stateSlide = state?.slides?.find((s) => s.id === planSlide.scene_id);

      return {
        id: planSlide.scene_id,
        slideNumber: index + 1,
        sceneId: planSlide.scene_id,
        title: planSlide.title,
        layoutType: planSlide.layout_type,
        status: (stateSlide?.status || 'pending') as any,
        path: stateSlide?.path,
        estimatedDuration: planSlide.estimated_duration,
        componentMapping: planSlide.component_mapping,
        hasHtml: false, // TODO: Check if file exists
        hasGsap: false, // TODO: Check if GSAP file exists
      };
    });

    console.log('✅ [WorkflowAPI] Merged', slides.length, 'slides');
    return slides;
  } catch (error) {
    console.error('❌ [WorkflowAPI] Error merging slide data:', error);
    return null;
  }
}

/**
 * Get slide generation status - count completed slides from state.json
 */
export async function getSlideGenerationStatus(conversationId: string): Promise<{
  totalSlides: number;
  completedSlides: number;
  slides: SlideProgress[];
}> {
  try {
    console.log('📊 [WorkflowAPI] Getting slide generation status for:', conversationId);
    const slides = await getMergedSlideData(conversationId);

    if (!slides) {
      console.warn('⚠️ [WorkflowAPI] No slides data returned from getMergedSlideData');
      return {
        totalSlides: 0,
        completedSlides: 0,
        slides: [],
      };
    }

    const completedSlides = slides.filter(s => s.status === 'complete').length;

    console.log('✅ [WorkflowAPI] Slide status:', {
      totalSlides: slides.length,
      completedSlides,
      pendingSlides: slides.length - completedSlides,
      slideDetails: slides.map(s => ({ id: s.id, number: s.slideNumber, status: s.status }))
    });

    return {
      totalSlides: slides.length,
      completedSlides,
      slides,
    };
  } catch (error) {
    console.error('❌ [WorkflowAPI] Error getting slide generation status:', error);
    return {
      totalSlides: 0,
      completedSlides: 0,
      slides: [],
    };
  }
}

/**
 * Get audio generation status from state.json
 */
export async function getAudioGenerationStatus(conversationId: string): Promise<{
  audioGenerated: boolean;
  audioGenerationComplete: boolean;
  audioUrl: string | null;
  duration: number | null;
  voiceId: string | null;
  language: string | null;
}> {
  try {
    console.log('📊 [WorkflowAPI] Getting audio generation status for:', conversationId);
    const state = await getState(conversationId);

    const result = {
      audioGenerated: state?.progress?.audioGenerated ?? false,
      audioGenerationComplete: state?.progress?.audioGenerationComplete ?? false,
      audioUrl: state?.voice_over?.url ?? null,
      duration: state?.voice_over?.duration ?? null,
      voiceId: state?.voice_over?.voice_id ?? null,
      language: state?.voice_over?.language ?? null,
    };

    console.log('✅ [WorkflowAPI] Audio status:', result);
    return result;
  } catch (error) {
    console.error('❌ [WorkflowAPI] Error getting audio generation status:', error);
    return {
      audioGenerated: false,
      audioGenerationComplete: false,
      audioUrl: null,
      duration: null,
      voiceId: null,
      language: null,
    };
  }
}

/**
 * Audio version metadata structure
 */
export interface AudioVersion {
  version_id: string;
  language_code: string;
  voice_id: string;
  voice_name: string;
  url: string;
  duration: number;
  created_at: string;
  instructions?: string;
  script_length: number;
  generation_metadata?: {
    task_id?: string;
    framework?: string;
    target_audience?: string;
  };
}

/**
 * Get all audio versions from voiceover-audio.json
 */
export async function getAudioVersions(conversationId: string): Promise<{
  success: boolean;
  versions: AudioVersion[];
  selectedVersionId: string | null;
  totalVersions: number;
}> {
  try {
    console.log('📊 [WorkflowAPI] Getting audio versions for:', conversationId);
    const response = await api.get(`/conversations/${conversationId}/audio/versions`);

    const data = response.data;
    console.log('✅ [WorkflowAPI] Audio versions retrieved:', data.totalVersions);
    return data;
  } catch (error) {
    console.error('❌ [WorkflowAPI] Error getting audio versions:', error);
    return {
      success: false,
      versions: [],
      selectedVersionId: null,
      totalVersions: 0,
    };
  }
}

/**
 * Select an audio version and update state.json
 */
export async function selectAudioVersion(conversationId: string, versionId: string): Promise<{
  success: boolean;
  message?: string;
  selectedVersion?: AudioVersion;
  error?: string;
}> {
  try {
    console.log('📝 [WorkflowAPI] Selecting audio version:', versionId);
    const response = await api.post(`/conversations/${conversationId}/audio/select`, {
      versionId
    });

    const data = response.data;
    console.log('✅ [WorkflowAPI] Audio version selected successfully');
    return data;
  } catch (error: any) {
    console.error('❌ [WorkflowAPI] Error selecting audio version:', error);
    return {
      success: false,
      error: error.response?.data?.error || error.message || 'Unknown error',
    };
  }
}

// Export all API functions
export const workflowAPI = {
  getState,
  updateState,
  checkPlanExists,
  getPlanningStatus,
  getPlan,
  updatePlan,
  getResourceIndex,
  startResourceAnalysis,
  generatePlan,
  generateSlide,
  generateAllSlides,
  getSlides,
  getMergedSlideData,
  getSlideGenerationStatus,
  getAudioGenerationStatus,
};
